import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Nav, Tab } from "react-bootstrap";
import { handleError, handleSuccess } from "../CommonJquery/CommonJquery";
import MasterLayout from "../masterLayout/MasterLayout";
import {
  create_linkedin_post,
  delete_linkedin_comment,
  delete_linkedin_post,
  get_linkedin_auth_url,
  get_linkedin_comments,
  get_linkedin_follower_demographics,
  get_linkedin_insights,
  get_linkedin_page_details,
  get_linkedin_posts,
  linkedin_oauth_callback,
  react_linkedin_post,
  reply_linkedin_comment,
  save_linkedin_page,
  server_post_data,
  upload_linkedin_media,
} from "../ServiceConnection/serviceconnection";
import Loader from "./Loader";
import "../components/CSS/gmb-premium.css";
import "../components/CSS/facebook-premium.css";
import "../components/CSS/linkedin-premium.css";

/* ---------------------------------------------------------------------------
   Static config
--------------------------------------------------------------------------- */

const SOCIAL_PROFILES = [
  { key: "google", label: "Google Business", icon: "mdi:google", ready: false },
  { key: "facebook", label: "Facebook", icon: "mdi:facebook", ready: false },
  { key: "instagram", label: "Instagram", icon: "mdi:instagram", ready: false },
  { key: "twitter", label: "Twitter", icon: "ri:twitter-x-fill", ready: false },
  { key: "linkedin", label: "LinkedIn", icon: "mdi:linkedin", ready: true },
  { key: "pinterest", label: "Pinterest", icon: "mdi:pinterest", ready: false },
];

const CHANNEL_TABS = [
  { key: "linkedin", label: "LinkedIn", ready: true },
  { key: "facebook", label: "Facebook", ready: false },
  { key: "instagram", label: "Instagram", ready: false },
  { key: "google", label: "Google", ready: false },
  { key: "twitter", label: "Twitter", ready: false },
  { key: "pinterest", label: "Pinterest", ready: false },
];

const POST_TYPES = [
  { key: "TEXT", label: "Text", icon: "mdi:text" },
  { key: "IMAGE", label: "Image", icon: "mdi:image-outline" },
  { key: "VIDEO", label: "Video", icon: "mdi:video-outline" },
  { key: "ARTICLE", label: "Link", icon: "mdi:link-variant" },
  { key: "POLL", label: "Poll", icon: "mdi:poll" },
];

const POLL_DURATIONS = [
  { key: "ONE_DAY", label: "1 day" },
  { key: "THREE_DAYS", label: "3 days" },
  { key: "SEVEN_DAYS", label: "1 week" },
  { key: "FOURTEEN_DAYS", label: "2 weeks" },
];

const TIPS = [
  {
    title: "Lead With A Hook",
    body: "LinkedIn truncates after roughly three lines — put your strongest sentence first so people tap See more.",
  },
  {
    title: "Native Beats Outbound",
    body: "Posts that keep readers on LinkedIn reach further than posts pushing straight to an external link.",
  },
  {
    title: "Polls Drive Comments",
    body: "Polls consistently pull the highest comment rate of any format, which in turn extends reach.",
  },
];

const emptyPost = {
  post_type: "TEXT",
  commentary: "",
  media_urn: "",
  media_title: "",
  article_url: "",
  article_title: "",
  article_description: "",
  poll_question: "",
  poll_duration: "SEVEN_DAYS",
  visibility: "PUBLIC",
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) =>
  new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const DEMO_SECTIONS = [
  { key: "bySeniority", label: "By Seniority" },
  { key: "byFunction", label: "By Job Function" },
  { key: "byIndustry", label: "By Industry" },
  { key: "byCompanySize", label: "By Company Size" },
];

/* ------------------------------------------------------------------------ */

function LinkedInPanel() {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("page");

  // page
  const [page, setPage] = useState(null);
  const [orgList, setOrgList] = useState([]);
  const [showOrgPicker, setShowOrgPicker] = useState(false);
  const pendingTokens = useRef({ accessToken: "", refreshToken: "" });

  // posts
  const [posts, setPosts] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // composer
  const [showComposer, setShowComposer] = useState(false);
  const [postForm, setPostForm] = useState(emptyPost);
  const [preview, setPreview] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [channel, setChannel] = useState("linkedin");

  // comments
  const [showComments, setShowComments] = useState(false);
  const [commentPost, setCommentPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [replyBox, setReplyBox] = useState({});

  // insights
  const [range, setRange] = useState({
    start_date: daysAgoISO(29),
    end_date: todayISO(),
  });
  const [insights, setInsights] = useState(null);
  const [demographics, setDemographics] = useState(null);

  const clickRef = useRef(0);

  /* ------------------------- common caller ------------------------- */
  const call = useCallback(async (url, fields = {}, files = {}) => {
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") fd.append(k, v);
    });
    Object.entries(files).forEach(([k, f]) => f && fd.append(k, f));

    try {
      const res = await server_post_data(url, fd);
      const parts = res.message.split("~@~");
      if (parseInt(parts[0]) === 1) {
        handleError(parts[1]);
        return null;
      }
      const parsed = JSON.parse(parts[1]);
      if (parsed.error) {
        handleError(parsed.message || "LinkedIn API error");
        return null;
      }
      return parsed;
    } catch (e) {
      handleError("network");
      return null;
    }
  }, []);

  /* ------------------------- loaders ------------------------- */
  const loadPage = useCallback(async () => {
    setLoading(true);
    const d = await call(get_linkedin_page_details);
    if (d?.page) setPage(d.page);
    setLoading(false);
  }, [call]);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    const d = await call(get_linkedin_posts, { limit: 50 });
    setPosts(d?.posts || []);
    setLoading(false);
  }, [call]);

  const loadInsights = useCallback(async () => {
    setLoading(true);
    const d = await call(get_linkedin_insights, range);
    setInsights(d?.insights || null);
    setLoading(false);
  }, [call, range]);

  const loadDemographics = useCallback(async () => {
    setLoading(true);
    const d = await call(get_linkedin_follower_demographics);
    setDemographics(d?.demographics || null);
    setLoading(false);
  }, [call]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (activeTab === "posts") loadPosts();
    if (activeTab === "insights") loadInsights();
    if (activeTab === "audience") loadDemographics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  /* ------------------------- OAuth popup flow ------------------------- */
  const exchangeCode = useCallback(
    async (code) => {
      setLoading(true);
      const d = await call(linkedin_oauth_callback, { code });
      setLoading(false);

      if (d?.organizations?.length) {
        pendingTokens.current = {
          accessToken: d.accessToken,
          refreshToken: d.refreshToken || "",
        };
        setOrgList(d.organizations);
        setShowOrgPicker(true);
      } else if (d) {
        handleError("No LinkedIn Company Page is administered by this account");
      }
    },
    [call],
  );

  // LinkedIn has no JS SDK — the popup posts the auth code back to us here.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.source !== "linkedin-oauth") return;

      if (data.error) {
        handleError(data.error);
        return;
      }
      if (data.code) exchangeCode(data.code);
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [exchangeCode]);

  const handleConnect = async () => {
    const d = await call(get_linkedin_auth_url);
    if (!d?.authUrl) return;

    const w = 600;
    const h = 700;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;

    const popup = window.open(
      d.authUrl,
      "linkedin-oauth",
      `width=${w},height=${h},left=${left},top=${top}`,
    );
    if (!popup) handleError("Please allow popups to connect LinkedIn");
  };

  const handleSelectOrg = async (o) => {
    setLoading(true);
    const d = await call(save_linkedin_page, {
      organization_id: o.organizationId,
      access_token: pendingTokens.current.accessToken,
      refresh_token: pendingTokens.current.refreshToken,
      name: o.name,
      vanity_name: o.vanityName,
    });
    setLoading(false);
    if (d) {
      handleSuccess("LinkedIn page connected successfully");
      setShowOrgPicker(false);
      loadPage();
    }
  };

  /* ------------------------- composer ------------------------- */
  const resetComposer = () => {
    setPostForm(emptyPost);
    setPreview("");
    setPollOptions(["", ""]);
    setChannel("linkedin");
  };

  const openComposer = () => {
    resetComposer();
    setShowComposer(true);
  };

  const onPickMedia = async (file) => {
    if (!file) return;
    const kind = (file.type || "").startsWith("video") ? "VIDEO" : "IMAGE";

    setLoading(true);
    const d = await call(
      upload_linkedin_media,
      { media_type: kind },
      { media_file: file },
    );
    setLoading(false);

    if (!d) return;
    setPostForm((s) => ({ ...s, media_urn: d.urn, post_type: kind }));
    setPreview(URL.createObjectURL(file));
  };

  const clearMedia = () => {
    setPreview("");
    setPostForm((s) => ({ ...s, media_urn: "" }));
  };

  const publish = async () => {
    const t = postForm.post_type;

    if (t !== "POLL" && !postForm.commentary.trim()) {
      handleError("Post content is required");
      return;
    }
    if (postForm.commentary.length > 3000) {
      handleError("Commentary cannot exceed 3000 characters");
      return;
    }
    if ((t === "IMAGE" || t === "VIDEO") && !postForm.media_urn) {
      handleError("Please upload a file first");
      return;
    }
    if (t === "ARTICLE" && !postForm.article_url.trim()) {
      handleError("Link URL is required");
      return;
    }
    if (t === "POLL") {
      if (!postForm.poll_question.trim()) {
        handleError("Poll question is required");
        return;
      }
      if (pollOptions.filter((o) => o.trim()).length < 2) {
        handleError("A poll needs at least 2 filled options");
        return;
      }
    }

    if (clickRef.current === 1) return;
    clickRef.current = 1;
    setLoading(true);

    const payload = {
      post_type: t,
      commentary: postForm.commentary,
      visibility: postForm.visibility,
    };
    if (t === "IMAGE" || t === "VIDEO") {
      payload.media_urn = postForm.media_urn;
      payload.media_title = postForm.media_title;
    }
    if (t === "ARTICLE") {
      payload.article_url = postForm.article_url;
      payload.article_title = postForm.article_title;
      payload.article_description = postForm.article_description;
    }
    if (t === "POLL") {
      payload.poll_question = postForm.poll_question;
      payload.poll_options = JSON.stringify(
        pollOptions.filter((o) => o.trim()),
      );
      payload.poll_duration = postForm.poll_duration;
    }

    const d = await call(create_linkedin_post, payload);

    setLoading(false);
    clickRef.current = 0;

    if (d) {
      handleSuccess("Published to LinkedIn successfully");
      setShowComposer(false);
      loadPosts();
    }
  };

  const removePost = async () => {
    setLoading(true);
    const d = await call(delete_linkedin_post, { post_urn: confirmDelete });
    setLoading(false);
    setConfirmDelete(null);
    if (d) {
      handleSuccess("Post deleted successfully");
      loadPosts();
    }
  };

  const toggleLike = async (p) => {
    setLoading(true);
    const d = await call(react_linkedin_post, {
      post_urn: p.postUrn,
      action: p.liked ? "unlike" : "like",
    });
    setLoading(false);
    if (d) {
      handleSuccess(d.message || "Updated successfully");
      loadPosts();
    }
  };

  /* ------------------------- comments ------------------------- */
  const openComments = async (p) => {
    setCommentPost(p);
    setShowComments(true);
    setLoading(true);
    const d = await call(get_linkedin_comments, {
      post_urn: p.postUrn,
      limit: 50,
    });
    setComments(d?.comments || []);
    setLoading(false);
  };

  const sendReply = async (objectUrn, parentUrn) => {
    const msg = (replyBox[objectUrn] || "").trim();
    if (!msg) return;
    setLoading(true);
    const d = await call(reply_linkedin_comment, {
      object_urn: commentPost.postUrn,
      message: msg,
      parent_comment_urn: parentUrn || "",
    });
    setLoading(false);
    if (d) {
      handleSuccess("Comment posted successfully");
      setReplyBox((s) => ({ ...s, [objectUrn]: "" }));
      openComments(commentPost);
    }
  };

  const removeComment = async (commentUrn) => {
    setLoading(true);
    const d = await call(delete_linkedin_comment, {
      post_urn: commentPost.postUrn,
      comment_urn: commentUrn,
    });
    setLoading(false);
    if (d) {
      handleSuccess("Comment deleted successfully");
      openComments(commentPost);
    }
  };

  /* ------------------------- helpers ------------------------- */
  const initials = (n) => (n || "?").trim().charAt(0).toUpperCase();

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "-";

  const actorName = (urn) => {
    if (!urn) return "LinkedIn Member";
    if (urn.includes("organization")) return page?.name || "Your Page";
    return "LinkedIn Member";
  };

  const isPoll = postForm.post_type === "POLL";
  const isMedia = ["IMAGE", "VIDEO"].includes(postForm.post_type);
  const isArticle = postForm.post_type === "ARTICLE";

  const shareTotals = insights?.shareStatistics?.totals;

  return (
    <MasterLayout>
      <div className="gmb-premium fb-premium li-premium">
        {loading && <Loader />}

        {/* ------------------------- Topbar ------------------------- */}
        <div className="gmb-topbar">
          <div className="gmb-topbar-title">
            <h5 className="gmb-display">LinkedIn Page</h5>
            <span className="gmb-royal-badge">
              <Icon icon="mdi:linkedin" /> Social Suite
            </span>
            {page && (
              <span className="gmb-live">
                <span className="gmb-live-dot" /> Live
              </span>
            )}
          </div>
          <div className="gmb-topbar-actions">
            <button
              className="btn btn-outline-primary-600"
              onClick={handleConnect}
            >
              <Icon icon="mdi:account-switch-outline" /> Switch Page
            </button>
            <button className="btn btn-primary-600" onClick={openComposer}>
              <Icon icon="ic:baseline-plus" /> Create Post
            </button>
          </div>
        </div>

        {/* ------------------------- Page hero ------------------------- */}
        {page && (
          <div className="fb-page-hero">
            <img
              src={page.logo || ""}
              alt={page.name}
              onError={(e) => (e.currentTarget.style.visibility = "hidden")}
            />
            <div>
              <h6>{page.name}</h6>
              <span>
                {page.vanityName ? `@${page.vanityName}` : "Company Page"}
              </span>
            </div>
            <div className="fb-page-stats">
              <div>
                <b className="gmb-mono">{page.followerCount ?? 0}</b>
                <small>Followers</small>
              </div>
              <div>
                <b className="gmb-mono">{posts.length}</b>
                <small>Posts Loaded</small>
              </div>
              <div>
                <b className="gmb-mono">{page.staffCountRange ? "✓" : "-"}</b>
                <small>Verified</small>
              </div>
            </div>
          </div>
        )}

        {/* ------------------------- Tabs ------------------------- */}
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Nav className="nav-tabs">
            <Nav.Item>
              <Nav.Link eventKey="page">
                <Icon icon="mdi:information-outline" /> Page
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="posts">
                <Icon icon="mdi:post-outline" /> Posts
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="insights">
                <Icon icon="mdi:chart-line" /> Analytics
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="audience">
                <Icon icon="mdi:account-group-outline" /> Audience
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* ------------------ PAGE ------------------ */}
            <Tab.Pane eventKey="page">
              {!page ? (
                <div className="gmb-empty">
                  No LinkedIn page is connected yet — use “Switch Page” above.
                  You must be an administrator of the Company Page.
                </div>
              ) : (
                <div className="gmb-card" style={{ padding: 20 }}>
                  <div className="gmb-detail-row">
                    <strong>Name</strong>
                    <span>{page.name}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Description</strong>
                    <span>{page.description || "-"}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Website</strong>
                    <span>
                      {page.website ? (
                        <a href={page.website} target="_blank" rel="noreferrer">
                          {page.website}
                        </a>
                      ) : (
                        "-"
                      )}
                    </span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Followers</strong>
                    <span>{page.followerCount ?? 0}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Company Size</strong>
                    <span>{page.staffCountRange || "-"}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Page URL</strong>
                    <span>
                      {page.pageUrl ? (
                        <a href={page.pageUrl} target="_blank" rel="noreferrer">
                          {page.pageUrl}
                        </a>
                      ) : (
                        "-"
                      )}
                    </span>
                  </div>
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ POSTS ------------------ */}
            <Tab.Pane eventKey="posts">
              <div className="d-flex justify-content-end mb-3">
                <button
                  className="btn btn-outline-primary-600 btn-sm"
                  onClick={loadPosts}
                >
                  <Icon icon="mdi:refresh" /> Refresh
                </button>
              </div>

              {posts.length === 0 ? (
                <div className="gmb-empty">No posts published yet.</div>
              ) : (
                <div className="row g-3">
                  {posts.map((p) => (
                    <div className="col-xl-4 col-lg-6" key={p.postUrn}>
                      <div className="gmb-card li-post-card">
                        <div className="li-post-head">
                          <img
                            className="li-post-logo"
                            src={page?.logo || ""}
                            alt=""
                            onError={(e) =>
                              (e.currentTarget.style.visibility = "hidden")
                            }
                          />
                          <div>
                            <b>{page?.name || "Your Page"}</b>
                            <small>{fmtDate(p.createdTime)}</small>
                          </div>
                        </div>

                        <div className="li-post-body">
                          {p.commentary || "(no text)"}
                        </div>

                        {p.articleUrl && (
                          <div className="li-post-article">
                            <b>{p.articleTitle || "Shared link"}</b>
                            <span>{p.articleUrl}</span>
                          </div>
                        )}

                        <div className="fb-post-engagement">
                          <span>
                            <Icon icon="mdi:thumb-up-outline" />{" "}
                            {p.likeCount ?? 0}
                          </span>
                          <span>
                            <Icon icon="mdi:comment-outline" />{" "}
                            {p.commentCount ?? 0}
                          </span>
                          {p.permalink && (
                            <a
                              className="gmb-competitor-maps-link ms-auto"
                              href={p.permalink}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View
                            </a>
                          )}
                        </div>

                        <div className="d-flex gap-2 mt-3">
                          <button
                            className="btn btn-outline-primary-600 btn-sm"
                            onClick={() => openComments(p)}
                          >
                            <Icon icon="mdi:comment-text-outline" /> Comments
                          </button>
                          <button
                            className="btn btn-outline-primary-600 btn-sm"
                            onClick={() => toggleLike(p)}
                          >
                            <Icon icon="mdi:thumb-up-outline" />
                          </button>
                          <button
                            className="btn btn-outline-danger-600 btn-sm"
                            onClick={() => setConfirmDelete(p.postUrn)}
                          >
                            <Icon icon="fluent:delete-24-regular" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ ANALYTICS ------------------ */}
            <Tab.Pane eventKey="insights">
              <div className="gmb-date-row">
                <div>
                  <label>Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={range.start_date}
                    onChange={(e) =>
                      setRange((s) => ({ ...s, start_date: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label>End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={range.end_date}
                    onChange={(e) =>
                      setRange((s) => ({ ...s, end_date: e.target.value }))
                    }
                  />
                </div>
                <button className="btn btn-primary-600" onClick={loadInsights}>
                  <Icon icon="mdi:magnify" /> Fetch
                </button>
              </div>

              {!insights ? (
                <div className="gmb-empty">
                  Select a date range and click Fetch to load analytics.
                </div>
              ) : (
                <>
                  <div className="row g-3">
                    {shareTotals &&
                      [
                        [
                          "Impressions",
                          shareTotals.impressionCount,
                          "mdi:eye-outline",
                        ],
                        [
                          "Unique Reach",
                          shareTotals.uniqueImpressionsCount,
                          "mdi:account-eye-outline",
                        ],
                        [
                          "Clicks",
                          shareTotals.clickCount,
                          "mdi:cursor-default-click-outline",
                        ],
                        [
                          "Reactions",
                          shareTotals.likeCount,
                          "mdi:thumb-up-outline",
                        ],
                        [
                          "Comments",
                          shareTotals.commentCount,
                          "mdi:comment-outline",
                        ],
                        [
                          "Reposts",
                          shareTotals.shareCount,
                          "mdi:share-outline",
                        ],
                        [
                          "Engagement Rate",
                          `${shareTotals.engagement}%`,
                          "mdi:chart-donut",
                        ],
                      ].map(([label, value, icon]) => (
                        <div className="col-xl-3 col-lg-4 col-md-6" key={label}>
                          <div className="gmb-card gmb-metric-card">
                            <div
                              className="gmb-metric-icon"
                              style={{
                                background: "#e8f2fc",
                                color: "var(--li-brand)",
                              }}
                            >
                              <Icon icon={icon} />
                            </div>
                            <div className="gmb-metric-label">{label}</div>
                            <div className="gmb-metric-total gmb-mono">
                              {value}
                            </div>
                            <div className="gmb-metric-range">
                              <span>{range.start_date}</span>
                              <span>{range.end_date}</span>
                            </div>
                          </div>
                        </div>
                      ))}

                    {insights.followerStatistics && (
                      <div className="col-xl-3 col-lg-4 col-md-6">
                        <div className="gmb-card gmb-metric-card">
                          <div
                            className="gmb-metric-icon"
                            style={{
                              background: "#e8f2fc",
                              color: "var(--li-brand)",
                            }}
                          >
                            <Icon icon="mdi:account-plus-outline" />
                          </div>
                          <div className="gmb-metric-label">New Followers</div>
                          <div className="gmb-metric-total gmb-mono">
                            {insights.followerStatistics.totalGain}
                          </div>
                          <div className="gmb-metric-range">
                            <span>
                              Organic {insights.followerStatistics.organicGain}
                            </span>
                            <span>
                              Paid {insights.followerStatistics.paidGain}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {insights.pageStatistics && (
                      <div className="col-xl-3 col-lg-4 col-md-6">
                        <div className="gmb-card gmb-metric-card">
                          <div
                            className="gmb-metric-icon"
                            style={{
                              background: "#e8f2fc",
                              color: "var(--li-brand)",
                            }}
                          >
                            <Icon icon="mdi:file-eye-outline" />
                          </div>
                          <div className="gmb-metric-label">Page Views</div>
                          <div className="gmb-metric-total gmb-mono">
                            {insights.pageStatistics.totalPageViews}
                          </div>
                          <div className="gmb-metric-range">
                            <span>{range.start_date}</span>
                            <span>{range.end_date}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </Tab.Pane>

            {/* ------------------ AUDIENCE ------------------ */}
            <Tab.Pane eventKey="audience">
              <div className="gmb-tab-sub">
                Who follows your page — the same breakdown LinkedIn shows in its
                own analytics tab.
              </div>

              {!demographics ? (
                <div className="gmb-empty">
                  No follower demographics available.
                </div>
              ) : (
                <div className="row g-3">
                  {DEMO_SECTIONS.map((sec) => {
                    const rows = demographics[sec.key] || [];
                    const max = Math.max(...rows.map((r) => r.total), 1);
                    return (
                      <div className="col-lg-6" key={sec.key}>
                        <div className="gmb-card li-demo-card">
                          <div className="li-demo-title">{sec.label}</div>
                          {rows.length === 0 ? (
                            <div className="gmb-char-count">
                              No data available.
                            </div>
                          ) : (
                            rows.map((r) => (
                              <div className="li-demo-row" key={r.urn}>
                                <div className="li-demo-row-head">
                                  <span>{r.urn.split(":").pop()}</span>
                                  <b>{r.total}</b>
                                </div>
                                <div className="li-demo-track">
                                  <span
                                    className="li-demo-fill"
                                    style={{
                                      width: `${(r.total / max) * 100}%`,
                                    }}
                                  />
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        {/* ==================== COMPOSER MODAL ==================== */}
        <Modal
          className="gmb-modal fb-composer-modal"
          size="xl"
          centered
          show={showComposer}
          onHide={() => setShowComposer(false)}
        >
          <Modal.Body>
            <div className="fb-composer">
              {/* ---------- LEFT: social profiles ---------- */}
              <div className="fb-composer-col fb-composer-rail">
                <div className="fb-composer-heading">
                  Select Social Profiles
                </div>

                {SOCIAL_PROFILES.map((sp) => {
                  const connected = sp.key === "linkedin" && !!page;
                  return (
                    <div
                      key={sp.key}
                      className={`fb-social-item ${sp.key} ${
                        connected ? "is-connected" : ""
                      }`}
                    >
                      <div className="fb-social-head">
                        <Icon icon={sp.icon} />
                        {sp.label}
                        {connected && (
                          <span className="fb-social-count">(1)</span>
                        )}
                      </div>
                      <div className="fb-social-foot">
                        <button
                          type="button"
                          disabled={!sp.ready}
                          onClick={sp.ready ? handleConnect : undefined}
                        >
                          {connected ? "Connected" : "Connect Account"}
                        </button>
                        <span className="fb-social-status">
                          <Icon
                            icon={
                              connected
                                ? "mdi:check-circle"
                                : "mdi:check-circle-outline"
                            }
                          />
                        </span>
                      </div>
                    </div>
                  );
                })}

                <div className="fb-rail-divider" />

                <div className="fb-field-title">Visibility</div>
                <select
                  className="form-control"
                  value={postForm.visibility}
                  onChange={(e) =>
                    setPostForm((s) => ({ ...s, visibility: e.target.value }))
                  }
                >
                  <option value="PUBLIC">Anyone</option>
                  <option value="LOGGED_IN">LinkedIn members only</option>
                </select>

                <div className="fb-toggle-row mt-4">
                  <span>Scheduling</span>
                  <button type="button" className="fb-switch" disabled />
                </div>
                <div className="gmb-char-count">
                  LinkedIn's API has no native scheduling — posts publish
                  immediately.
                </div>
              </div>

              {/* ---------- MIDDLE: editor ---------- */}
              <div className="fb-composer-col">
                <div className="fb-composer-title">Create Post</div>
                <div className="fb-composer-sub">
                  {page ? `${page.name} selected` : "No page selected"}
                </div>

                <div className="fb-channel-tabs">
                  {CHANNEL_TABS.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      disabled={!c.ready}
                      className={channel === c.key ? "active" : ""}
                      onClick={() => c.ready && setChannel(c.key)}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>

                <div className="fb-account-row">
                  <Icon
                    icon="mdi:linkedin"
                    style={{ color: "var(--li-brand)" }}
                  />
                  LinkedIn Page:
                  <span className="fb-account-chip">
                    {page ? page.name : "Not connected"}
                  </span>
                </div>

                <div className="fb-field-title">Select Post Type</div>
                <div className="fb-pills">
                  {POST_TYPES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      className={`fb-pill ${
                        postForm.post_type === t.key ? "active" : ""
                      }`}
                      onClick={() => {
                        setPostForm((s) => ({
                          ...s,
                          post_type: t.key,
                          media_urn: "",
                        }));
                        setPreview("");
                      }}
                    >
                      {postForm.post_type === t.key ? (
                        <Icon icon="mdi:check" />
                      ) : (
                        <Icon icon={t.icon} />
                      )}
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* ---- Media ---- */}
                {isMedia && (
                  <>
                    <div className="fb-field-title">
                      Add Post{" "}
                      {postForm.post_type === "VIDEO" ? "Video" : "Image"} (
                      Recommended Size - 1200 X 627 )
                    </div>

                    {preview ? (
                      <div className="fb-drop-preview">
                        {postForm.post_type === "VIDEO" ? (
                          <video src={preview} controls />
                        ) : (
                          <img src={preview} alt="preview" />
                        )}
                        <button
                          type="button"
                          className="fb-drop-remove"
                          onClick={clearMedia}
                        >
                          <Icon icon="mdi:close" />
                        </button>
                      </div>
                    ) : (
                      <label className="fb-dropzone">
                        <input
                          type="file"
                          accept={
                            postForm.post_type === "VIDEO"
                              ? "video/*"
                              : "image/*"
                          }
                          onChange={(e) => onPickMedia(e.target.files[0])}
                        />
                        <div className="fb-drop-icon">
                          <Icon icon="mdi:image-plus-outline" />
                        </div>
                        <div className="fb-drop-title">
                          Upload or Drag{" "}
                          {postForm.post_type === "VIDEO" ? "Video" : "Photo"}
                        </div>
                        <div className="fb-drop-hint">
                          Maximum Size - 200 MB. Format JPG, PNG, MP4
                        </div>
                      </label>
                    )}

                    <div className="mt-3">
                      <div className="fb-field-title">
                        Media Title (optional)
                      </div>
                      <input
                        className="form-control"
                        placeholder="Eg: Product walkthrough"
                        value={postForm.media_title}
                        onChange={(e) =>
                          setPostForm((s) => ({
                            ...s,
                            media_title: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </>
                )}

                {/* ---- Article ---- */}
                {isArticle && (
                  <>
                    <div className="fb-field-title">Link URL</div>
                    <input
                      className="form-control mb-3"
                      placeholder="https://example.com/blog/post"
                      value={postForm.article_url}
                      onChange={(e) =>
                        setPostForm((s) => ({
                          ...s,
                          article_url: e.target.value,
                        }))
                      }
                    />
                    <div className="fb-field-title">Link Title (optional)</div>
                    <input
                      className="form-control mb-3"
                      placeholder="Eg: How we cut onboarding time in half"
                      value={postForm.article_title}
                      onChange={(e) =>
                        setPostForm((s) => ({
                          ...s,
                          article_title: e.target.value,
                        }))
                      }
                    />
                    <div className="fb-field-title">
                      Link Description (optional)
                    </div>
                    <input
                      className="form-control"
                      placeholder="A short summary shown under the title"
                      value={postForm.article_description}
                      onChange={(e) =>
                        setPostForm((s) => ({
                          ...s,
                          article_description: e.target.value,
                        }))
                      }
                    />
                  </>
                )}

                {/* ---- Poll ---- */}
                {isPoll && (
                  <>
                    <div className="fb-field-title">Poll Question</div>
                    <input
                      className="form-control mb-3"
                      placeholder="Eg: What slows your team down the most?"
                      value={postForm.poll_question}
                      onChange={(e) =>
                        setPostForm((s) => ({
                          ...s,
                          poll_question: e.target.value,
                        }))
                      }
                    />

                    <div className="fb-field-title">Options ( 2 - 4 )</div>
                    {pollOptions.map((opt, i) => (
                      <div className="li-poll-option" key={i}>
                        <input
                          className="form-control"
                          placeholder={`Option ${i + 1}`}
                          value={opt}
                          onChange={(e) =>
                            setPollOptions((s) =>
                              s.map((o, idx) =>
                                idx === i ? e.target.value : o,
                              ),
                            )
                          }
                        />
                        <button
                          type="button"
                          disabled={pollOptions.length <= 2}
                          onClick={() =>
                            setPollOptions((s) =>
                              s.filter((_, idx) => idx !== i),
                            )
                          }
                        >
                          <Icon icon="mdi:close" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="li-poll-add"
                      disabled={pollOptions.length >= 4}
                      onClick={() => setPollOptions((s) => [...s, ""])}
                    >
                      <Icon icon="mdi:plus" /> Add Option
                    </button>

                    <div className="fb-field-title mt-3">Poll Duration</div>
                    <select
                      className="form-control"
                      value={postForm.poll_duration}
                      onChange={(e) =>
                        setPostForm((s) => ({
                          ...s,
                          poll_duration: e.target.value,
                        }))
                      }
                    >
                      {POLL_DURATIONS.map((d) => (
                        <option key={d.key} value={d.key}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                <div className="fb-field-title mt-4">Write a Post</div>
                <textarea
                  className="form-control"
                  rows={5}
                  placeholder="Eg: We just shipped something our customers have been asking for..."
                  value={postForm.commentary}
                  onChange={(e) =>
                    setPostForm((s) => ({ ...s, commentary: e.target.value }))
                  }
                />
                <div className="gmb-char-count">
                  {postForm.commentary.length} / 3000 characters
                </div>

                <div className="fb-submit-bar">
                  <button
                    type="button"
                    className="btn btn-outline-primary-600"
                    onClick={() => setShowComposer(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="fb-submit"
                    disabled={!page}
                    onClick={publish}
                  >
                    <Icon icon="mdi:linkedin" />
                    Publish To LinkedIn
                  </button>
                </div>
              </div>

              {/* ---------- RIGHT: preview + tips ---------- */}
              <div className="fb-composer-col fb-composer-aside">
                <div className="fb-preview">
                  <div className="fb-preview-head">
                    <Icon
                      icon="mdi:linkedin"
                      style={{ color: "var(--li-brand)" }}
                    />
                    LinkedIn Preview
                  </div>

                  <div
                    className="li-post-head"
                    style={{ padding: "12px 14px 0" }}
                  >
                    <img
                      className="li-post-logo"
                      src={page?.logo || ""}
                      alt=""
                      onError={(e) =>
                        (e.currentTarget.style.visibility = "hidden")
                      }
                    />
                    <div>
                      <b>{page?.name || "Your Company"}</b>
                      <small>
                        {page?.followerCount ?? 0} followers · Just now
                      </small>
                    </div>
                  </div>

                  <div className="fb-preview-body">
                    <p>{postForm.commentary || "Your content here"}</p>

                    {isPoll && (
                      <div className="li-preview-poll">
                        <b>{postForm.poll_question || "Your poll question"}</b>
                        {pollOptions
                          .filter((o) => o.trim())
                          .map((o, i) => (
                            <div key={i}>{o}</div>
                          ))}
                      </div>
                    )}

                    {isArticle && postForm.article_url && (
                      <div className="li-post-article mt-3">
                        <b>{postForm.article_title || "Shared link"}</b>
                        <span>{postForm.article_url}</span>
                      </div>
                    )}
                  </div>

                  {preview &&
                    (postForm.post_type === "VIDEO" ? (
                      <video className="fb-preview-img" src={preview} muted />
                    ) : (
                      <img className="fb-preview-img" src={preview} alt="" />
                    ))}
                </div>

                <div className="fb-composer-heading">Tips to Improve Reach</div>
                {TIPS.map((t) => (
                  <div className="fb-tip-card" key={t.title}>
                    <div className="fb-tip-icon">
                      <Icon icon="mdi:lightbulb-on-outline" />
                    </div>
                    <h6>{t.title}</h6>
                    <p>{t.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* ==================== COMMENTS MODAL ==================== */}
        <Modal
          className="gmb-modal"
          size="lg"
          centered
          show={showComments}
          onHide={() => setShowComments(false)}
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>Comments</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex gap-2 mb-3">
              <input
                className="form-control"
                placeholder="Write a comment on this post..."
                value={replyBox[commentPost?.postUrn] || ""}
                onChange={(e) =>
                  setReplyBox((s) => ({
                    ...s,
                    [commentPost.postUrn]: e.target.value,
                  }))
                }
              />
              <button
                className="btn btn-primary-600"
                onClick={() => sendReply(commentPost.postUrn, "")}
              >
                Post
              </button>
            </div>

            {comments.length === 0 ? (
              <div className="gmb-empty">No comments yet.</div>
            ) : (
              comments.map((c) => (
                <div className="fb-comment" key={c.commentUrn}>
                  <div className="fb-comment-avatar">
                    {initials(actorName(c.actor))}
                  </div>
                  <div className="fb-comment-body">
                    <b>{actorName(c.actor)}</b>
                    <p>{c.text}</p>
                    <div className="fb-comment-actions">
                      <button
                        onClick={() =>
                          setReplyBox((s) => ({
                            ...s,
                            [c.commentUrn]: s[c.commentUrn] || "",
                          }))
                        }
                      >
                        Reply
                      </button>
                      <button
                        className="danger"
                        onClick={() => removeComment(c.commentUrn)}
                      >
                        Delete
                      </button>
                      <span style={{ color: "var(--gmb-ink-3)" }}>
                        {fmtDate(c.createdTime)}
                      </span>
                    </div>
                    {replyBox[c.commentUrn] !== undefined && (
                      <div className="d-flex gap-2 mt-2">
                        <input
                          className="form-control"
                          placeholder="Write a reply..."
                          value={replyBox[c.commentUrn]}
                          onChange={(e) =>
                            setReplyBox((s) => ({
                              ...s,
                              [c.commentUrn]: e.target.value,
                            }))
                          }
                        />
                        <button
                          className="btn btn-primary-600 btn-sm"
                          onClick={() => sendReply(c.commentUrn, c.commentUrn)}
                        >
                          Send
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </Modal.Body>
        </Modal>

        {/* ==================== ORG PICKER ==================== */}
        <Modal
          className="gmb-modal"
          centered
          show={showOrgPicker}
          onHide={() => setShowOrgPicker(false)}
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>Select a Company Page</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {orgList.map((o) => (
              <div
                className="gmb-card gmb-location-card mb-2 d-flex align-items-center gap-3"
                key={o.organizationId}
                style={{ cursor: "pointer" }}
                onClick={() => handleSelectOrg(o)}
              >
                <img
                  src={o.logo}
                  alt=""
                  style={{ width: 42, height: 42, borderRadius: 6 }}
                  onError={(e) => (e.currentTarget.style.visibility = "hidden")}
                />
                <div>
                  <h6 className="mb-0">{o.name}</h6>
                  <div className="gmb-location-meta">
                    {o.vanityName
                      ? `@${o.vanityName}`
                      : `ID ${o.organizationId}`}
                  </div>
                </div>
              </div>
            ))}
          </Modal.Body>
        </Modal>

        {/* ==================== DELETE CONFIRM ==================== */}
        <Modal
          className="gmb-modal"
          size="sm"
          centered
          show={!!confirmDelete}
          onHide={() => setConfirmDelete(null)}
        >
          <Modal.Body className="text-center">
            <Icon
              icon="fluent:delete-24-regular"
              style={{ fontSize: 34, color: "var(--danger-main)" }}
            />
            <p className="mt-2 mb-0">
              Please confirm, do you want to delete this post?
            </p>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-outline-primary-600"
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </button>
            <button className="btn btn-danger-600" onClick={removePost}>
              Confirm
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </MasterLayout>
  );
}

export default LinkedInPanel;
